import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ROOM,
  ROOM_ITEM_SPECS,
  SHAPE_PATHS,
  WALLS,
  clampToRoom,
  isSeat,
  itemLabel,
  snap,
  studentInitials,
  studentName,
  type ItemShape,
  type RoomItem,
  type RoomSize,
  type Student,
  type Wall,
} from '@td/shared';
import { studentPhotoUrl } from '../hooks/useStudents';
import { usePrivacy } from '../privacy/PrivacyContext';

/**
 * A landscape Letter page at 96dpi, less the 12mm margins, is roughly
 * 966 × 725pt. The room is scaled to fit inside that, whatever size the
 * teacher has told us the room is — a seating chart is one room, so it is
 * one sheet.
 */
const PAGE = { width: 960, height: 720 } as const;

function printScaleFor(room: RoomSize): number {
  return Math.min(PAGE.width / room.width, PAGE.height / room.height);
}

/**
 * The room, drawn to scale and dragged with pointer events.
 *
 * Pointer events rather than a drag-and-drop library: this is one rectangle
 * moving inside another, and `setPointerCapture` handles the mouse leaving
 * the element mid-drag for free. Students, by contrast, use HTML5 drag —
 * they travel between the canvas and the sidebar list, which pointer capture
 * can't span.
 */
export function RoomCanvas({
  items,
  room,
  zoom = 1,
  wallLabels,
  students,
  selectedId,
  editing,
  flagged,
  onSelect,
  onMove,
  onDropStudent,
  onWallLabel,
}: {
  items: RoomItem[];
  /** The room's own size in units, derived from its measurements in feet. */
  room: RoomSize;
  /** 1 fits the room to the panel; above that it grows and the view scrolls. */
  zoom?: number;
  wallLabels: Record<Wall, string>;
  students: Student[];
  selectedId: string | null;
  editing: boolean;
  /** Seats caught up in a broken rule, outlined so they can be spotted. */
  flagged?: string[];
  onSelect: (id: string | null) => void;
  onMove: (id: string, x: number, y: number) => void;
  /** `fromItemId` is set when the student was dragged off another seat. */
  onDropStudent: (itemId: string, studentId: string, fromItemId: string | null) => void;
  onWallLabel: (wall: Wall, label: string) => void;
}) {
  const viewRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [dragging, setDragging] = useState<string | null>(null);
  const [hoverSeat, setHoverSeat] = useState<string | null>(null);
  const dragOffset = useRef({ x: 0, y: 0 });
  const printing = useRef(false);

  const measure = useCallback(() => {
    const el = viewRef.current;
    if (!el) return;
    // Measured on the scroll container, not the room: once zoomed in, the
    // room is wider than the space it sits in, and measuring the room itself
    // would feed its own size back into the calculation.
    setScale(printing.current ? printScaleFor(room) : (el.clientWidth / room.width) * zoom);
  }, [room, zoom]);

  // The room keeps its proportions and fills whatever width it is given, so
  // the same chart reads the same on a laptop, a projector, and paper.
  useEffect(() => {
    const el = viewRef.current;
    if (!el) return;
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure]);

  // Everything inside is positioned from `scale`, so switching it is all it
  // takes to make the room fit a sheet of paper instead of the viewport.
  useEffect(() => {
    function before() {
      printing.current = true;
      setScale(printScaleFor(room));
    }
    function after() {
      printing.current = false;
      measure();
    }
    window.addEventListener('beforeprint', before);
    window.addEventListener('afterprint', after);
    return () => {
      window.removeEventListener('beforeprint', before);
      window.removeEventListener('afterprint', after);
    };
  }, [measure, room]);

  const byId = useCallback((id: string) => items.find((i) => i.id === id), [items]);

  function onPointerDown(e: React.PointerEvent, item: RoomItem) {
    if (!editing) {
      onSelect(item.id);
      return;
    }
    e.preventDefault();
    (e.target as Element).setPointerCapture(e.pointerId);
    const rect = frameRef.current!.getBoundingClientRect();
    dragOffset.current = {
      x: (e.clientX - rect.left) / scale - item.x,
      y: (e.clientY - rect.top) / scale - item.y,
    };
    setDragging(item.id);
    onSelect(item.id);
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!dragging) return;
    const item = byId(dragging);
    if (!item) return;

    const rect = frameRef.current!.getBoundingClientRect();
    const next = clampToRoom(
      {
        ...item,
        x: snap((e.clientX - rect.left) / scale - dragOffset.current.x),
        y: snap((e.clientY - rect.top) / scale - dragOffset.current.y),
      },
      room,
    );
    onMove(dragging, next.x, next.y);
  }

  function endDrag(e: React.PointerEvent) {
    if (!dragging) return;
    (e.target as Element).releasePointerCapture?.(e.pointerId);
    setDragging(null);
  }

  return (
    <div
      ref={viewRef}
      className="w-full overflow-auto rounded-card print:overflow-visible"
      // Zoomed in, the room is bigger than the screen and this scrolls; at
      // fit, it is exactly the width available and nothing scrolls.
      style={{ maxHeight: printing.current ? undefined : '78vh' }}
    >
      <div
        ref={frameRef}
        className="relative select-none overflow-hidden rounded-card border border-sand bg-white print-exact print-keep-together"
        style={{
          width: room.width * scale,
          height: room.height * scale,
        }}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onClick={(e) => {
          if (e.target === e.currentTarget) onSelect(null);
        }}
      >
        {WALLS.map((wall) => (
          <WallBand
            key={wall}
            wall={wall}
            label={wallLabels[wall]}
            scale={scale}
            editing={editing}
            onLabel={(v) => onWallLabel(wall, v)}
          />
        ))}

        {items.map((item) => {
          const spec = ROOM_ITEM_SPECS[item.kind];
          const seat = isSeat(item);
          const student = seat ? students.find((s) => s.id === item.studentId) : undefined;
          const turned = item.rotation === 90 || item.rotation === 270;
          const path = SHAPE_PATHS[spec.shape];

          return (
            <div
              key={item.id}
              role="button"
              tabIndex={0}
              aria-label={itemLabel(item)}
              onPointerDown={(e) => onPointerDown(e, item)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelect(item.id);
                }
              }}
              onDragOver={(e) => {
                if (!seat) return;
                e.preventDefault();
                setHoverSeat(item.id);
              }}
              onDragLeave={() => setHoverSeat((h) => (h === item.id ? null : h))}
              onDrop={(e) => {
                if (!seat) return;
                e.preventDefault();
                setHoverSeat(null);
                const studentId = e.dataTransfer.getData('text/student-id');
                const from = e.dataTransfer.getData('text/from-item') || null;
                if (studentId) onDropStudent(item.id, studentId, from);
              }}
              className={[
                'absolute flex flex-col items-center justify-center text-center transition-shadow',
                editing ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer',
                dragging === item.id ? 'shadow-lifted' : '',
                selectedId === item.id ? 'ring-2 ring-espresso' : '',
                hoverSeat === item.id ? 'ring-2 ring-terracotta' : '',
                flagged?.includes(item.id) ? 'ring-2 ring-rust' : '',
                path ? '' : shapeClass(spec.shape),
              ].join(' ')}
              style={{
                left: item.x * scale,
                top: item.y * scale,
                width: (turned ? item.h : item.w) * scale,
                height: (turned ? item.w : item.h) * scale,
                // Path shapes paint their own fill in the SVG underneath.
                backgroundColor: path
                  ? undefined
                  : student
                    ? '#FFFFFF'
                    : `${spec.color}${seat ? '3D' : '66'}`,
                border: path ? undefined : `1.5px solid ${spec.color}`,
                fontSize: Math.max(8, 11 * scale),
                touchAction: 'none',
              }}
            >
              {path && (
                <svg
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                  aria-hidden
                  className="pointer-events-none absolute inset-0 h-full w-full"
                >
                  <path
                    d={path}
                    fill={student ? '#FFFFFF' : `${spec.color}${seat ? '3D' : '66'}`}
                    stroke={spec.color}
                    strokeWidth={2}
                    vectorEffect="non-scaling-stroke"
                  />
                </svg>
              )}

              <span className="relative flex flex-col items-center justify-center">
                {student ? (
                  <SeatContents student={student} item={item} scale={scale} />
                ) : (
                  <span className="px-1 leading-tight text-walnut">
                    {spec.glyph ? (
                      <span style={{ fontSize: Math.max(10, 18 * scale) }}>{spec.glyph}</span>
                    ) : seat ? (
                      ''
                    ) : (
                      itemLabel(item)
                    )}
                  </span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function shapeClass(shape: ItemShape): string {
  if (shape === 'circle') return 'rounded-full';
  if (shape === 'rounded') return 'rounded-lg';
  return 'rounded-sm';
}

/**
 * The child in a seat. Draggable in its own right, so a student can be pulled
 * onto another desk or back to the unseated list without disturbing the
 * furniture underneath — `stopPropagation` on pointerdown keeps the desk from
 * starting its own drag.
 */
function SeatContents({
  student,
  item,
  scale,
}: {
  student: Student;
  item: RoomItem;
  scale: number;
}) {
  const { privacy } = usePrivacy();
  const size = Math.max(16, 34 * scale);

  return (
    <span
      draggable
      onPointerDown={(e) => e.stopPropagation()}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/student-id', student.id);
        e.dataTransfer.setData('text/from-item', item.id);
        e.dataTransfer.effectAllowed = 'move';
      }}
      title={`${studentName(student)} — drag to another desk, or back to the list`}
      className="flex cursor-grab flex-col items-center active:cursor-grabbing print:cursor-default"
    >
      {student.hasPhoto ? (
        <img
          src={studentPhotoUrl(student)}
          alt=""
          draggable={false}
          className={['rounded-full object-cover', privacy ? 'blur-[3px]' : ''].join(' ')}
          style={{ width: size, height: size }}
        />
      ) : (
        <span
          className="flex items-center justify-center rounded-full bg-sand/70 text-espresso"
          style={{ width: size, height: size, fontSize: Math.max(7, 12 * scale) }}
        >
          {studentInitials(student)}
        </span>
      )}
      <span className="mt-0.5 max-w-full truncate px-1 leading-tight text-espresso">
        {privacy ? `${studentName(student)[0]}.` : studentName(student)}
        {student.number != null && !privacy ? ` ${student.number}` : ''}
      </span>
    </span>
  );
}

/** One labelled wall. Click the label to rename it while editing. */
function WallBand({
  wall,
  label,
  scale,
  editing,
  onLabel,
}: {
  wall: Wall;
  label: string;
  scale: number;
  editing: boolean;
  onLabel: (value: string) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const band = ROOM.wall * scale;

  const position: React.CSSProperties =
    wall === 'top'
      ? { top: 0, left: 0, right: 0, height: band }
      : wall === 'bottom'
        ? { bottom: 0, left: 0, right: 0, height: band }
        : wall === 'left'
          ? { top: 0, bottom: 0, left: 0, width: band }
          : { top: 0, bottom: 0, right: 0, width: band };

  const vertical = wall === 'left' || wall === 'right';

  return (
    <div
      className="pointer-events-none absolute flex items-center justify-center bg-linen/70"
      style={position}
    >
      {draft === null ? (
        <button
          onClick={() => editing && setDraft(label)}
          disabled={!editing}
          className={[
            'pointer-events-auto max-w-full truncate px-2 uppercase tracking-widest text-stone',
            editing ? 'hover:text-espresso' : 'cursor-default',
          ].join(' ')}
          style={{
            fontSize: Math.max(7, 11 * scale),
            writingMode: vertical ? 'vertical-rl' : undefined,
            rotate: wall === 'left' ? '180deg' : undefined,
          }}
          title={editing ? 'Rename this wall' : undefined}
        >
          {label}
        </button>
      ) : (
        <input
          autoFocus
          value={draft}
          maxLength={30}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            onLabel(draft.trim());
            setDraft(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') setDraft(null);
          }}
          className="pointer-events-auto w-24 rounded border border-clay bg-white px-1 text-center text-[11px] text-espresso"
        />
      )}
    </div>
  );
}
